import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Upload, Trash2 } from 'lucide-react';
import { getToken } from '../../lib/authStore';

const API = process.env.REACT_APP_BACKEND_URL;

// Common mortgage-program tags — auto-suggested via a datalist so LOs can
// pick from the ones every card is already using, or type any custom tag.
const SUGGESTED_TAGS = [
  'Conventional', 'VA', 'FHA', 'USDA', 'Jumbo', 'Super Jumbo',
  'Bank Statement', 'DSCR', 'NON QM', 'P&L', 'HELOC', 'HEL', 'Fixed HELOC',
  'FHA < 580', 'VA < 580', 'Bridge Loan', 'Rehab Loan', 'Reverse Mortgage',
  'Assets as Income', 'Variable Income', 'ARM VA/FHA', 'Non Warrantable Condo',
  'Condotel', 'Hard Money', 'Fix and Flip', 'No Income',
];

const CHANNELS = [
  { key: 'broker',        label: 'Broker (wholesale)' },
  { key: 'correspondent', label: 'Correspondent' },
  { key: 'both',          label: 'Broker · Correspondent' },
];

export default function LenderModal({ mode, lender, isAdmin, onClose, onSaved, onPatched }) {
  const isEdit = mode === 'edit';
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState(() => ({
    name: lender?.name || '',
    channel: lender?.channel || 'broker',
    compPct: lender?.compPct ?? '',
    specialties: lender?.specialties || [],
    ae: { name: '', phone: '', email: '', ...(lender?.ae || {}) },
    logoPath: lender?.logoPath || null,
  }));
  const [tagInput, setTagInput] = useState('');

  // Trap ESC + prevent body scroll while open.
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  const canEditAll = isAdmin;         // full form
  const canEditAeOnly = !isAdmin && isEdit; // LO on existing lender

  const addTag = (raw) => {
    const t = (raw || '').trim();
    if (!t) return;
    setForm((f) => f.specialties.includes(t) ? f : { ...f, specialties: [...f.specialties, t] });
    setTagInput('');
  };
  const removeTag = (t) => setForm((f) => ({ ...f, specialties: f.specialties.filter((x) => x !== t) }));

  const save = async () => {
    if (saving) return;
    if (canEditAll && !form.name.trim()) {
      toast.error('Lender name is required');
      return;
    }
    setSaving(true);
    try {
      const payload = canEditAll ? {
        name: form.name.trim(),
        channel: form.channel,
        compPct: form.compPct === '' ? null : Number(form.compPct),
        specialties: form.specialties,
        ae: form.ae,
      } : {
        ae: form.ae, // LO branch — server also enforces this scope
      };
      const r = isEdit
        ? await axios.patch(`${API}/api/lenders/${lender.id}`, payload)
        : await axios.post(`${API}/api/lenders`, payload);
      onSaved?.(r.data);
      toast.success(isEdit ? 'Lender updated' : 'Lender added');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    if (!isEdit) {
      toast.error('Save the lender first, then upload a logo');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Logo must be under 2 MB');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/api/lenders/${lender.id}/logo`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setForm((f) => ({ ...f, logoPath: r.data.logoPath }));
      // Bubble the patched doc up as an IN-PLACE merge — do NOT call onSaved
      // here, which would close the modal and lose the LO's in-progress form
      // edits (name / comp / specialties / AE changes not yet saved).
      onPatched?.(r.data);
      toast.success('Logo updated');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const remove = async () => {
    if (!isEdit || !isAdmin) return;
    if (!window.confirm(`Delete lender "${lender.name}"?`)) return;
    try {
      await axios.delete(`${API}/api/lenders/${lender.id}`);
      onSaved?.(null, lender.id);
      toast.success('Lender deleted');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  // Query-param auth on the logo URL — `<img>` can't send Authorization.
  const token = getToken();
  const logoUrl = isEdit && form.logoPath && token
    ? `${API}/api/lenders/${lender.id}/logo?auth=${encodeURIComponent(token)}&t=${Date.now()}`
    : null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[100] flex items-start justify-center bg-black/50 backdrop-blur-sm overflow-y-auto p-4 animate-fadeIn"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg my-8 bg-white rounded-2xl border hairline shadow-[0_18px_48px_-18px_rgba(0,0,0,0.35)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between px-5 py-4 border-b hairline-soft">
          <div>
            <h2 className="text-[15px] font-bold text-neutral-900">
              {isEdit ? (canEditAll ? 'Edit lender' : 'Update AE contact') : 'New lender'}
            </h2>
            <p className="text-[11.5px] text-neutral-500 mt-0.5">
              {canEditAeOnly
                ? 'Only account executive contact info can be edited by non-admins.'
                : 'Track lenders, their comp, specialties, and account executive.'}
            </p>
          </div>
          <button
            type="button"
            data-testid="lender-modal-close"
            onClick={onClose}
            className="h-7 w-7 rounded flex items-center justify-center text-neutral-400 hover:text-neutral-900 hover:bg-neutral-100 transition"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Basic info — admin-only. `disabled` on each input (not just a
              CSS overlay) so LOs can't Tab-focus / paste into these fields;
              the server also strips non-`ae` keys as a defense in depth. */}
          <fieldset disabled={!canEditAll} className={!canEditAll ? 'space-y-3 opacity-50' : 'space-y-3'}>
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wider text-neutral-500 mb-1">Lender name</label>
              <input
                data-testid="lender-modal-name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="RKT, Acra, EPM…"
                className="w-full h-10 px-3 rounded-lg border hairline bg-white text-[13px] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--kf-gold))]"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-neutral-500 mb-1">Channel</label>
                <select
                  data-testid="lender-modal-channel"
                  value={form.channel}
                  onChange={(e) => setForm((f) => ({ ...f, channel: e.target.value }))}
                  className="w-full h-10 px-3 rounded-lg border hairline bg-white text-[13px] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--kf-gold))]"
                >
                  {CHANNELS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-neutral-500 mb-1">Comp %</label>
                <input
                  data-testid="lender-modal-comp"
                  type="number"
                  step="0.001"
                  value={form.compPct}
                  onChange={(e) => setForm((f) => ({ ...f, compPct: e.target.value }))}
                  placeholder="2.750"
                  className="w-full h-10 px-3 rounded-lg border hairline bg-white text-[13px] tabular-nums focus:outline-none focus:ring-2 focus:ring-[hsl(var(--kf-gold))]"
                />
              </div>
            </div>

            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wider text-neutral-500 mb-1">Specialties</label>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {form.specialties.map((t) => (
                  <span
                    key={t}
                    className="inline-flex items-center gap-1 text-[11px] rounded border hairline bg-neutral-50 text-neutral-700 pl-2 pr-1 py-0.5"
                  >
                    {t}
                    <button
                      type="button"
                      onClick={() => removeTag(t)}
                      className="hover:text-red-600 transition h-4 w-4 rounded flex items-center justify-center"
                      title={`Remove ${t}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
              <input
                data-testid="lender-modal-tag-input"
                list="lender-tag-suggestions"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ',') {
                    e.preventDefault();
                    addTag(tagInput);
                  }
                }}
                onBlur={() => tagInput && addTag(tagInput)}
                placeholder="Add a specialty — Enter or comma to add"
                className="w-full h-9 px-3 rounded-lg border hairline bg-white text-[12.5px] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--kf-gold))]"
              />
              <datalist id="lender-tag-suggestions">
                {SUGGESTED_TAGS.map((t) => <option key={t} value={t} />)}
              </datalist>
            </div>
          </fieldset>

          {/* Account executive — LO-editable */}
          <div className="border-t hairline-soft pt-4">
            <div className="text-[11px] font-bold uppercase tracking-wider text-neutral-500 mb-2">Account executive</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input
                data-testid="lender-modal-ae-name"
                value={form.ae.name || ''}
                onChange={(e) => setForm((f) => ({ ...f, ae: { ...f.ae, name: e.target.value } }))}
                placeholder="AE name"
                className="w-full h-10 px-3 rounded-lg border hairline bg-white text-[13px] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--kf-gold))]"
              />
              <input
                data-testid="lender-modal-ae-phone"
                value={form.ae.phone || ''}
                onChange={(e) => setForm((f) => ({ ...f, ae: { ...f.ae, phone: e.target.value } }))}
                placeholder="(555) 000-0000"
                className="w-full h-10 px-3 rounded-lg border hairline bg-white text-[13px] tabular-nums focus:outline-none focus:ring-2 focus:ring-[hsl(var(--kf-gold))]"
              />
              <input
                data-testid="lender-modal-ae-email"
                type="email"
                value={form.ae.email || ''}
                onChange={(e) => setForm((f) => ({ ...f, ae: { ...f.ae, email: e.target.value } }))}
                placeholder="ae@lender.com"
                className="w-full h-10 px-3 rounded-lg border hairline bg-white text-[13px] sm:col-span-2 focus:outline-none focus:ring-2 focus:ring-[hsl(var(--kf-gold))]"
              />
            </div>
          </div>

          {/* Logo — admin-only, edit only (upload endpoint needs a lender id) */}
          {canEditAll && (
            <div className="border-t hairline-soft pt-4">
              <div className="text-[11px] font-bold uppercase tracking-wider text-neutral-500 mb-2">Logo</div>
              <div className="flex items-center gap-3">
                <div className="h-16 w-16 rounded-lg border hairline bg-neutral-50 flex items-center justify-center overflow-hidden">
                  {logoUrl ? (
                    <img src={logoUrl} alt="" className="max-h-full max-w-full object-contain" />
                  ) : (
                    <span className="text-[10px] text-neutral-400">No logo</span>
                  )}
                </div>
                <label className="flex-1">
                  <input
                    data-testid="lender-modal-logo-input"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => uploadLogo(e.target.files?.[0])}
                    disabled={!isEdit || uploading}
                  />
                  <span
                    className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border hairline bg-white text-[12.5px] font-semibold transition ${isEdit && !uploading ? 'hover:bg-neutral-50 cursor-pointer text-neutral-800' : 'opacity-50 cursor-not-allowed text-neutral-500'}`}
                  >
                    <Upload className="h-3.5 w-3.5" />
                    {uploading ? 'Uploading…' : (logoUrl ? 'Replace logo' : 'Upload logo')}
                  </span>
                  {!isEdit && (
                    <div className="text-[10.5px] text-neutral-400 mt-1">
                      Save the lender first, then upload a logo.
                    </div>
                  )}
                </label>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 px-5 py-3 border-t hairline-soft bg-neutral-50/50 rounded-b-2xl">
          {isEdit && isAdmin ? (
            <button
              type="button"
              data-testid="lender-modal-delete"
              onClick={remove}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-[12.5px] font-semibold text-red-600 hover:bg-red-50 transition"
            >
              <Trash2 className="h-3.5 w-3.5" /> Delete
            </button>
          ) : <div />}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="h-9 px-3 rounded-lg border hairline bg-white text-[12.5px] font-semibold text-neutral-700 hover:bg-neutral-50 transition"
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid="lender-modal-save"
              onClick={save}
              disabled={saving}
              className="h-9 px-4 rounded-lg bg-neutral-900 text-[hsl(var(--kf-gold))] text-[12.5px] font-bold hover:bg-neutral-800 transition disabled:opacity-60"
            >
              {saving ? 'Saving…' : (isEdit ? 'Save changes' : 'Add lender')}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
